import { env } from '$env/dynamic/public';

/**
 * Site-wide SEO constants.
 *
 * Everything Yoast used to inject now originates here or from page frontmatter,
 * so the plugin can be removed without losing metadata.
 */

export const SITE_URL = 'https://dutchpassion.co.th';
export const SITE_NAME = 'Dutch Passion Thailand';

/**
 * Fallback social image, used when a page supplies none.
 *
 * **Deliberately the JPEG, while every on-page use of this photograph points at
 * the WebP beside it.** `scripts/optimize-media.mjs` converted the file because
 * WebP halves it for visitors, but this URL is not read by a browser — it is
 * read by link unfurlers, and LinkedIn does not render WebP previews at all. A
 * card that fails to unfurl is worse than 60 KB nobody on the site downloads:
 * this file is fetched by crawlers only.
 */
export const DEFAULT_OG_IMAGE = '/media/dutch-passion-seed-bank-thailand_banner01-1400x613.jpg';

/** Absolute URL for a site-relative path. */
export function absolute(path: string): string {
	return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * GA4 measurement ID, e.g. `G-QSJE0VPBVL`.
 *
 * Overridable with `PUBLIC_GA_MEASUREMENT_ID`, but it falls back to the real
 * property, the same way `checkout/config.ts` falls back to the real Omise
 * publishable key. Neither is a secret: this ID is in the page source of every
 * page on the site, and it identifies a destination rather than granting access
 * to anything.
 *
 * **The committed default is the point.** With the value living only in a
 * gitignored `.env`, a build from any other checkout or machine silently dropped
 * analytics — which is exactly what happened on 21 August 2026. A default that is
 * correct means a fresh clone builds a correct site, and `.env` becomes an
 * override for anyone who needs a different property rather than the only place
 * the truth exists.
 *
 * `$env/dynamic/public` rather than static so an absent variable is not a build
 * error. The site is fully prerendered, so this resolves at build time and is
 * baked into every page: changing it needs a rebuild, not just a redeploy. And
 * it cannot come from `wrangler.toml` — `[vars]` there are runtime Worker
 * bindings, delivered to `src/worker.ts` on each request, long after the HTML
 * that has to carry this string was written.
 */
export const GA_MEASUREMENT_ID = env.PUBLIC_GA_MEASUREMENT_ID || 'G-TF56R9EQ9M';

/**
 * The ID used to *load* gtag.js, which is not always the measurement ID.
 *
 * `<script src="…/gtag/js?id=X">` wants the property's **Google tag** ID, and
 * `gtag('config', …)` wants the **measurement ID**. Usually a measurement ID
 * works for both, so the distinction never comes up — but when a property's
 * Google tag was created as a `GT-` tag (this one also carries an `AW-` Ads
 * destination), Google publishes the loader only under that ID and
 * `…/gtag/js?id=G-TF56R9EQ9M` answers **404**. The browser then never defines
 * `gtag`, `Analytics.svelte` bails out on its `typeof gtag === 'undefined'`
 * guard, and the site records nothing while the GA dashboard cheerfully reports
 * the stream as active — because other sources are still feeding it.
 *
 * Find it, if it ever changes, in the destination bundle, which is served under
 * the measurement ID even when the loader is not:
 *
 *   curl -s 'https://www.googletagmanager.com/gtag/destination?id=<MEASUREMENT_ID>' \
 *     | grep -o 'GT-[A-Z0-9]\{6,\}' | sort -u
 *
 * Committed for the same reason as the measurement ID above. A property whose
 * loader *is* published under its measurement ID can simply set
 * `PUBLIC_GA_TAG_ID` to the same value as `PUBLIC_GA_MEASUREMENT_ID`.
 *
 * `scripts/check-env.mjs` fetches whichever ID this resolves to and warns if it
 * does not answer 200, which is the check that would have caught the 404.
 */
export const GA_TAG_ID = env.PUBLIC_GA_TAG_ID || 'GT-NFRRVLJM';
